import React from "react";
import ActionCard from "../ActionCard";

const ELIGIBLE_STATES = new Set(["restricted", "archived"]);

function label(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "sms") return "SMS";
  if (normalized === "whatsapp") return "WhatsApp";
  return normalized
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function actionRecords(summary) {
  const seen = new Set();
  return (summary?.records || []).flatMap((record) => {
    const contactId = String(record?.contact_id || "");
    if (!contactId || seen.has(contactId)) return [];

    const states = Object.entries(record?.states || {})
      .filter(([, state]) => ELIGIBLE_STATES.has(String(state)))
      .sort(([left], [right]) => left.localeCompare(right));
    if (states.length === 0) return [];

    seen.add(contactId);
    return [{
      contact_id: contactId,
      name: String(record?.name || "(no name)"),
      issue: "Communication restricted or contact archived",
      current: states.map(([channel, state]) => `${label(channel)} — ${label(state)}`).join("; "),
      suggested: "Keep suppressed; delete only after retention review",
      applicable: false,
    }];
  });
}

function coverageDescription(summary) {
  const coverage = summary?.coverage === "partial" ? "Partial" : "Complete";
  const unavailable = (summary?.unavailable_channels || [])
    .map((channel) => `${label(channel)} unavailable`);
  const counts = summary?.contact_counts || {};
  const details = [
    `${coverage} provider coverage`,
    ...unavailable,
    `${Number(counts.subscribed || 0)} subscribed`,
    `${Number(counts.not_specified || 0)} not specified`,
  ];
  return `${details.join(" · ")}. Status totals are informational and may overlap.`;
}

export default function ConsentDndCard({
  summary,
  legacy = false,
  connectionId,
  scanId,
  index = 0,
  bulkDeleteCapability = { available: false, reason: null },
  onApplied,
}) {
  if (legacy || !summary || !["complete", "partial"].includes(summary.coverage)) {
    return (
      <aside role="note" aria-label="Consent & DND" data-testid="consent-dnd-card"
        className="rounded-customer border border-slate-200 bg-slate-50 px-ui-4 py-ui-3 text-sm text-slate-600">
        <strong className="font-semibold text-slate-900">Consent status {legacy ? "not collected" : "unavailable"}.</strong>{" "}
        {legacy
          ? "Consent and DND status was not collected for this scan. Run a new scan to check provider safeguards."
          : "The connected CRM did not make consent status available for this scan. CleanIQ completed the data-quality scan without changing any contact preferences."}
      </aside>
    );
  }

  const finding = {
    key: "consent_dnd",
    category: "Communication safeguards",
    severity: "info",
    title: "Consent & DND",
    affected_count: Number(summary.record_count || 0),
    applicable_count: 0,
    records_paginated: Boolean(summary.records_paginated),
    records: actionRecords(summary),
    description: coverageDescription(summary),
    recommendation: "Keep restricted contacts suppressed and review legal or business retention requirements before deleting them.",
  };

  return (
    <ActionCard
      finding={finding}
      index={index}
      connectionId={connectionId}
      scanId={scanId}
      bulkDeleteCapability={bulkDeleteCapability}
      onApplied={onApplied}
    />
  );
}
